import { Component, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { SmartIrrigationService } from 'src/api/services';
import {
  SmartIrrigationControllerDashboard,
  SmartIrrigationDashboard,
  SmartIrrigationDevice,
  SmartIrrigationDeviceDto,
  SmartIrrigationModel,
  SmartIrrigationRootNodeDto,
  SmartIrrigationSoilMoistureProbe,
  SmartIrrigationWaterMeter,
  SmartIrrigationWeatherStation
} from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { forkJoin } from 'rxjs';

const CONTROLLER_OVERDUE_MS = 15 * 60 * 1000;
const WEATHER_STATION_OVERDUE_MS = 30 * 60 * 1000;
const SOIL_PROBE_OVERDUE_MS = 6 * 60 * 60 * 1000;
const WATER_METER_OVERDUE_MS = 12 * 60 * 60 * 1000;

interface DeviceHealthRow {
  parkName: string;
  deviceName: string;
  deviceType: string;
  lastSeen: Date;
  status: 'ok' | 'overdue';
}

@Component({
  selector: 'app-smart-irrigation-system-health',
  templateUrl: './system-health.component.html',
  styleUrls: ['./system-health.component.scss']
})
export class SmartIrrigationSystemHealthComponent implements OnInit {
  Highcharts = Highcharts;
  chartConstructor = 'chart';

  loading = true;
  lastRefresh: Date | null = null;
  rows: DeviceHealthRow[] = [];
  okCount = 0;
  overdueCount = 0;
  pieChartOptions: Highcharts.Options | null = null;

  constructor(
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    forkJoin({
      rootNode: this.irrigation.SmartIrrigationGetRootNode(this.app.customerId),
      dashboard: this.irrigation.SmartIrrigationGetDashboard(this.app.customerId),
      model: this.irrigation.SmartIrrigationGetModel(this.app.customerId)
    }).subscribe({
      next: ({ rootNode, dashboard, model }) => {
        this.rows = this.buildRows(rootNode, dashboard, model)
          .sort((a, b) => a.parkName.localeCompare(b.parkName) || a.deviceName.localeCompare(b.deviceName));
        this.okCount = this.rows.filter(row => row.status === 'ok').length;
        this.overdueCount = this.rows.filter(row => row.status === 'overdue').length;
        this.pieChartOptions = this.buildPieChartOptions();
        this.lastRefresh = new Date();
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.okCount = 0;
        this.overdueCount = 0;
        this.pieChartOptions = null;
        this.loading = false;
      }
    });
  }

  private buildRows(rootNode: SmartIrrigationRootNodeDto, dashboard: SmartIrrigationDashboard, model: SmartIrrigationModel): DeviceHealthRow[] {
    const controllersByUri = this.indexByUri(dashboard.irrigationControllers || [], controller => controller.siteUri);
    const weatherStationsByUri = this.indexByUri(dashboard.weatherStations || [], station => station.siteUri || station.deviceEui);
    const waterMetersByUri = this.indexByUri(Object.values(model?.waterMeters || {}), meter => meter.siteUri);
    const soilProbesByUri = this.indexByUri(Object.values(model?.soilMoistureProbes || {}), probe => probe.siteUri);

    const rows: DeviceHealthRow[] = [];

    // Devices live either directly on a Park node (weather station, water meter) or on a Park's
    // Field children (controller, soil moisture probe) - so a "park" is any node whose children host a controller.
    const visitPark = (park: SmartIrrigationRootNodeDto): void => {
      (park.associatedDevices || []).forEach(device => {
        const row = this.buildRow(park.name || `Park ${park.id}`, device, controllersByUri, weatherStationsByUri, waterMetersByUri, soilProbesByUri);
        if (row) {
          rows.push(row);
        }
      });
      (park.childNodes || []).forEach(field => {
        (field.associatedDevices || []).forEach(device => {
          const row = this.buildRow(park.name || `Park ${park.id}`, device, controllersByUri, weatherStationsByUri, waterMetersByUri, soilProbesByUri);
          if (row) {
            rows.push(row);
          }
        });
      });
    };

    const visit = (node: SmartIrrigationRootNodeDto): void => {
      const children = node.childNodes || [];
      if (children.some(child => this.nodeContainsController(child))) {
        visitPark(node);
      }
      children.forEach(visit);
    };

    visit(rootNode);
    return rows;
  }

  // Returns null for devices that have never reported, which are excluded from the report entirely.
  private buildRow(
    parkName: string,
    device: SmartIrrigationDeviceDto,
    controllersByUri: Map<string, SmartIrrigationControllerDashboard>,
    weatherStationsByUri: Map<string, SmartIrrigationWeatherStation>,
    waterMetersByUri: Map<string, SmartIrrigationWaterMeter>,
    soilProbesByUri: Map<string, SmartIrrigationSoilMoistureProbe>
  ): DeviceHealthRow | null {
    const uri = this.normalize(device.uri);
    const typeName = device.deviceTypeName || 'Unknown';
    let match: SmartIrrigationDevice | undefined;
    let overdueThresholdMs = CONTROLLER_OVERDUE_MS;

    if (this.isController(typeName)) {
      match = controllersByUri.get(uri);
      overdueThresholdMs = CONTROLLER_OVERDUE_MS;
    } else if (this.isWeatherStation(typeName)) {
      match = weatherStationsByUri.get(uri);
      overdueThresholdMs = WEATHER_STATION_OVERDUE_MS;
    } else if (this.isWaterMeter(typeName)) {
      match = waterMetersByUri.get(uri);
      overdueThresholdMs = WATER_METER_OVERDUE_MS;
    } else if (this.isSoilSensor(typeName)) {
      match = soilProbesByUri.get(uri);
      overdueThresholdMs = SOIL_PROBE_OVERDUE_MS;
    }

    if (!match?.lastSeen) {
      return null;
    }

    const lastSeen = new Date(match.lastSeen);
    const overdue = (Date.now() - lastSeen.getTime()) > overdueThresholdMs;

    return {
      parkName,
      deviceName: match?.description || match?.name || match?.deviceEui || device.uri || `Device ${device.id}`,
      deviceType: typeName,
      lastSeen,
      status: overdue ? 'overdue' : 'ok'
    };
  }

  private buildPieChartOptions(): Highcharts.Options {
    return {
      chart: { type: 'pie', backgroundColor: 'transparent', style: { fontFamily: 'inherit' } },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: { pointFormat: '{series.name}: <b>{point.y}</b> ({point.percentage:.0f}%)' },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: { enabled: true, format: '{point.name}: {point.y}' }
        }
      },
      series: [{
        name: 'Devices',
        type: 'pie',
        data: [
          { name: 'Up to date', y: this.okCount, color: '#38a169' },
          { name: 'Overdue', y: this.overdueCount, color: '#e53e3e' }
        ]
      } as Highcharts.SeriesPieOptions]
    };
  }

  private indexByUri<T>(items: T[], getUri: (item: T) => string | undefined): Map<string, T> {
    const map = new Map<string, T>();
    items.forEach(item => {
      const uri = this.normalize(getUri(item));
      if (uri) {
        map.set(uri, item);
      }
    });
    return map;
  }

  private nodeContainsController(node: SmartIrrigationRootNodeDto): boolean {
    return (node.associatedDevices || []).some(device => this.isController(device.deviceTypeName));
  }

  private isController(typeName?: string): boolean {
    return (typeName || '').toLowerCase().includes('controller');
  }

  private isWeatherStation(typeName?: string): boolean {
    return (typeName || '').toLowerCase().includes('weather');
  }

  private isWaterMeter(typeName?: string): boolean {
    return (typeName || '').toLowerCase().includes('water meter');
  }

  private isSoilSensor(typeName?: string): boolean {
    const type = (typeName || '').toLowerCase();
    return type.includes('soil') || type.includes('probe');
  }

  private normalize(value?: string): string {
    return (value || '').trim().toLowerCase();
  }
}
